/**
 * As rotas `/demo/*` da API: os passos do pipeline de captação, um por
 * chamada, para rodar o piloto de ponta a ponta numa máquina local.
 *
 * Fora do contrato OpenAPI de propósito (só existem em Development, com o
 * parser configurado) — por isso os tipos moram aqui, e não no
 * `@anery/api-contracts`.
 */

import { requisitar } from '../../../../shared/api/clienteHttp';
import { ErroDaApi } from '../../../../shared/api/erros';

export interface ResultadoDaColeta {
  readonly lidas: number;
  readonly coletadas: number;
  readonly anexos: number;
  readonly falhas: number;
  /** A coleta parou no lote: ainda há e-mail novo na caixa para o próximo clique. */
  readonly sobrouNaCaixa: boolean;
  /** Os e-mails que este clique gravou — o lote em que o resto do clique trabalha. */
  readonly emailIds: readonly string[];
}

export interface ResultadoDaTriagem {
  readonly lidos: number;
  readonly abertos: number;
  readonly vinculados: number;
  readonly falhas: number;
  readonly sobrouFila: boolean;
}

export interface ResultadoDaLeitura {
  readonly acabou: boolean;
  readonly emailId: string | null;
  /** O cursor da próxima chamada, como veio (o servidor guarda microssegundos). */
  readonly recebidoEm: string | null;
  readonly ticket: string | null;
  /** `Receipt`, `InProgress`, `OutOfProcess`, `Undetermined` — ou `null`. */
  readonly classe: string | null;
  readonly falha: string | null;
  /** O agente foi chamado nesta leitura — no modo da demo, só nos recebimentos. */
  readonly agenteRodou: boolean;
}

export interface ResultadoDaPromocao {
  readonly avaliados: number;
  readonly promovidos: number;
  readonly foraDaCaptacao: number;
  readonly nomeOuHospitalAConfirmar: number;
  readonly operadoraNaoResolvida: number;
  readonly iwIndisponivel: number;
}

/** Resolve se a API ligou a leitura da caixa; 404 (desligada) vira `false`. */
export async function leituraDaCaixaLigada(sinal?: AbortSignal): Promise<boolean> {
  try {
    await requisitar<{ ligado: boolean }>('/demo', { sinal });
    return true;
  } catch (erro) {
    if (erro instanceof ErroDaApi && erro.status === 404) return false;
    throw erro;
  }
}

export function coletarCaixa(sinal?: AbortSignal): Promise<ResultadoDaColeta> {
  return requisitar<ResultadoDaColeta>('/demo/coleta', { method: 'POST', sinal });
}

/** Tria só o lote do clique. */
export function triarCaixa(emailIds: readonly string[], sinal?: AbortSignal): Promise<ResultadoDaTriagem> {
  return requisitar<ResultadoDaTriagem>('/demo/triagem', { method: 'POST', corpo: { emailIds }, sinal });
}

/** A próxima origem sem classe DENTRO do lote do clique. */
export function lerProximaOrigem(
  antesDe: string | null,
  emailIds: readonly string[],
  sinal?: AbortSignal,
): Promise<ResultadoDaLeitura> {
  const busca = antesDe === null ? '' : `?${new URLSearchParams({ antesDe }).toString()}`;
  return requisitar<ResultadoDaLeitura>(`/demo/interpretacao/proxima${busca}`, {
    method: 'POST',
    corpo: { emailIds },
    sinal,
  });
}

/** Relê UM e-mail da thread de um ticket (qualquer um, origem ou não), pelo mesmo caminho da leitura da fila. */
export function lerEmailDoTicket(
  ticketId: string,
  emailId: string,
  sinal?: AbortSignal,
): Promise<Omit<ResultadoDaLeitura, 'acabou'> & { readonly naoEncontrado: boolean }> {
  return requisitar(
    `/demo/interpretacao/triagem/${encodeURIComponent(ticketId)}/emails/${encodeURIComponent(emailId)}`,
    { method: 'POST', sinal },
  );
}

export function promoverTickets(sinal?: AbortSignal): Promise<ResultadoDaPromocao> {
  return requisitar<ResultadoDaPromocao>('/demo/promocao', { method: 'POST', sinal });
}
